import React from 'react';

import type { EvidenceStatus } from '../../form/types';
import { isNeedsMoreDetail } from '../statusHelpers';
import EvidenceStatusControl from './EvidenceStatusControl';

interface EvidenceRowProps {
  id: string;
  title: string;
  value: EvidenceStatus;
  onChange: (value: EvidenceStatus) => void;
  contextPlaceholder?: string;
  hintLines?: React.ReactNode;
}

/**
 * EvidenceRow
 * Shared row wrapper used by both grouped rows and coverage rows
 * to enforce consistent visuals and interactions.
 */
const EvidenceRow: React.FC<EvidenceRowProps> = ({
  id,
  title,
  value,
  onChange,
  contextPlaceholder,
  hintLines,
}) => {
  const needsMoreDetail = isNeedsMoreDetail(value);
  const bgClass = 'bg-white border border-gray-200';

  return (
    <div
      className={`relative pl-1 flex items-start justify-between gap-3 p-2 rounded-md transition-colors ${bgClass}`}
    >
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-md ${
          needsMoreDetail ? 'bg-amber-400' : 'bg-transparent'
        }`}
        aria-hidden="true"
      />
      <div className="min-w-0 ml-2">
        <div className="text-sm font-medium text-gray-900">{title}</div>
        {needsMoreDetail && (
          <span className="inline-flex items-center mt-0.5 rounded-md bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-medium text-amber-800">
            Needs more detail
          </span>
        )}
        {hintLines}
      </div>
      <div className="w-full sm:w-72 md:w-80 flex-shrink-0">
        <EvidenceStatusControl
          id={id}
          label={title}
          value={value}
          onChange={onChange}
          notesVisibility="optional"
          hideLabel
          contextPlaceholder={contextPlaceholder}
          disabled={value.status === 'CONFIRMED'}
        />
      </div>
    </div>
  );
};

export default EvidenceRow;
